// ABOUTME: Onboarding wizard page for first-time users after signup.
// ABOUTME: Guides users through name collection, data source connection, and first investigation.

import { useState, useCallback, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Database, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DataSourceWizard } from '@/components/data-source/DataSourceWizard';
import { userApi, workspaceMetricsApi } from '@/services/api/client';
import type { DataSource } from '@/types/api';

type OnboardingStep = 'profile' | 'data-source' | 'data-source-connect' | 'complete';

// Map frontend step names to backend step names for tracking
const stepToBackendStep: Record<OnboardingStep, 'not_started' | 'profile' | 'welcome' | 'data_source' | 'completed'> = {
  'profile': 'profile',
  'data-source': 'data_source',
  'data-source-connect': 'data_source',
  'complete': 'completed',
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Check if we should skip to data-source step (e.g., coming from HomePage)
  const skipToDataSource = searchParams.get('step') === 'data-source';
  // When mode=add, user is adding an additional data source (already has one)
  const isAddMode = searchParams.get('mode') === 'add';

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('profile');
  const [firstName, setFirstName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingOnboardingStatus, setIsCheckingOnboardingStatus] = useState(true);

  // Track onboarding progress in the database for churn analysis
  const trackOnboardingProgress = useCallback(async (step: OnboardingStep) => {
    const backendStep = stepToBackendStep[step];
    try {
      await userApi.updateOnboardingProgress({ step: backendStep });
    } catch (err) {
      // Don't block the user flow if tracking fails
      console.error('Failed to track onboarding progress:', err);
    }
  }, []);

  // Check onboarding status and determine where to resume
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Check if user has data sources (completed onboarding)
        const metrics = await workspaceMetricsApi.get();
        if (metrics.data_sources > 0 && !isAddMode) {
          navigate('/', { replace: true });
          return;
        }

        // Handle explicit skip to data-source step
        if (skipToDataSource) {
          setCurrentStep('data-source');
          trackOnboardingProgress('data-source');
          setIsCheckingOnboardingStatus(false);
          return;
        }

        // Fetch user profile to get onboarding_step
        const profile = await userApi.me();
        const backendStep = profile.preferences.onboarding_step;

        let resumeStep: OnboardingStep = 'profile';
        if (backendStep === 'welcome' || backendStep === 'data_source' || backendStep === 'completed') {
          resumeStep = 'data-source';
        }

        setCurrentStep(resumeStep);
        if (backendStep === 'not_started') {
          trackOnboardingProgress(resumeStep);
        }
      } catch (err) {
        console.debug('Failed to check onboarding status:', err);
        trackOnboardingProgress('profile');
      } finally {
        setIsCheckingOnboardingStatus(false);
      }
    };

    checkOnboardingStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, skipToDataSource, isAddMode]);

  const handleSaveName = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!firstName.trim()) {
      setNameError('Please enter your name');
      return;
    }

    setIsSavingName(true);
    setNameError(null);

    try {
      await userApi.updateProfile({ name: firstName.trim() });
      setCurrentStep('data-source');
      trackOnboardingProgress('data-source');
    } catch (err) {
      console.error('Failed to save name:', err);
      setNameError('Failed to save your name. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  }, [firstName, trackOnboardingProgress]);

  const handleDataSourceComplete = useCallback((_dataSource: DataSource) => {
    trackOnboardingProgress('complete');
    navigate(isAddMode ? '/data-sources' : '/');
  }, [navigate, trackOnboardingProgress, isAddMode]);

  const handleDataSourceCancel = useCallback(() => {
    setCurrentStep('data-source');
  }, []);

  const handleConnectData = useCallback(() => {
    setCurrentStep('data-source-connect');
    trackOnboardingProgress('data-source');
  }, [trackOnboardingProgress]);

  // Show loading state while checking if user should be on this page
  if (isCheckingOnboardingStatus) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-surface-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Profile step - collect user's name
  if (currentStep === 'profile') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-md mx-auto px-6 py-16">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/oleum-logo.png"
              alt="Oleum BI"
              className="w-20 h-20 object-contain"
            />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-surface-900 mb-3">
              Welcome to Oleum BI
            </h1>
            <p className="text-surface-600">
              What should we call you?
            </p>
          </div>

          {/* Name Form */}
          <form onSubmit={handleSaveName} className="space-y-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-surface-700 mb-2">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                autoFocus
                className="w-full px-4 py-3 rounded-sm border border-surface-300 bg-surface-0 text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {nameError && (
                <p className="mt-2 text-sm text-error">{nameError}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSavingName}
            >
              {isSavingName ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Data source connection sub-step
  if (currentStep === 'data-source-connect') {
    return (
      <div className="h-full overflow-y-auto flex items-center justify-center p-6">
        <DataSourceWizard
          onComplete={handleDataSourceComplete}
          onCancel={handleDataSourceCancel}
        />
      </div>
    );
  }

  // Data source chooser
  if (currentStep === 'data-source') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-surface-900 mb-3">
              Connect your data
            </h2>
            <p className="text-surface-600">
              Connect a data source to start asking questions.
            </p>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleConnectData}
              className="text-left p-6 rounded-sm border-2 border-surface-200 bg-surface-0 hover:border-primary-300 hover:bg-primary-50/50 transition-all group cursor-pointer max-w-sm w-full"
            >
              <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mb-4 group-hover:bg-surface-200 transition-colors">
                <Database className="w-6 h-6 text-surface-600" />
              </div>
              <h3 className="text-lg font-semibold text-surface-900 mb-2">
                Connect your data
              </h3>
              <p className="text-sm text-surface-600">
                Connect your workspace and start analyzing your data right away.
              </p>
            </button>
          </div>

        </div>
      </div>
    );
  }

  // Fallback
  return null;
}
